import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  Plus,
  Camera,
  WifiOff,
  AlertTriangle,
  Trash2,
  KeyRound,
  Upload,
  CheckCircle2,
  AlertCircle,
  Clock,
  Sparkles,
  CloudUpload,
  X,
  FolderOpen,
  Loader2,
  RefreshCw,
  Pencil,
} from "lucide-react";



import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from "../Marketplace/MarketplaceEmptyState";
import {
  SessionsGrid,
  SessionCard,
  ExpiredOverlay,
  ExpiryWarning,
  CardTopRow,
  DeviceIconWrap,
  CardMeta,
  DeviceName,
  DeviceBrand,
  CardTools,
  ConnTypeBadge,
  StatsRow,
  StatChip,
  AlbumRow,
  AlbumLabel,
  AlbumValue,
  C2CPageContainer,

  Hero,
  HeroGlow,
  HeroContent,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  SummaryStatsRow,
  WalletHero,
  WalletIconWrap,
  WalletHeroBody,
  WalletHeroValue,
  WalletHeroLabel,
  StatCard,
  StatIcon,
  StatBody,
  StatValue,
  StatLabel,
} from "./CameraToCloudStyles";
import {
  Backdrop,
  Dot,
  DropdownMenu,
  ItemIconContainer,
  ItemLabel,
  MenuContainer,
  MenuItem,
  ThreeDotsButton,
} from "../documentSharedStyles";
import {
  ListPanel,
  ListHeader,
  ListTitle,
  CountBadge,
} from "../../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroRow,
  HeroText as ModalHeroText,
  HeroBadge as ModalHeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import { SubmitButton } from "../../../molecules/Dashboard/Member/memberFormStyles";
import {
  listC2CSessions,
  getC2CSession,
  revokeC2CSession,
  getC2CUploads,
  C2CSession,
  C2CSessionWithFtps,
  C2CFtps,
  C2CUploadLog,
} from "../../../../Redux/ApiCalls/Dashboard/C2CAPI";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import { CreateSessionModal } from "./CreateSessionModal";
import { CredentialsModal } from "./CredentialsModal";
import { UploadProgressPanel } from "./UploadProgressPanel";
import { EditSessionModal } from "./EditSessionModal";
import { ExpiryAlertModal, ExpiringSessionAlertItem } from "./ExpiryAlertModal";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const DisabledHero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 72px 28px;
  text-align: center;
  border-radius: 20px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.1) 0%,
    rgba(206, 86, 176, 0.08) 55%,
    rgba(53, 183, 200, 0.05) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.06);
`;

const DisabledIconWrap = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(201, 77, 170, 0.14);
  color: #c94daa;
`;

const DisabledTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  color: #171717;
`;

const DisabledDesc = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  color: rgba(23, 23, 23, 0.62);
  max-width: 460px;
  line-height: 1.7;
`;

const RevokeModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  padding: 0 24px 24px;
`;

const RevokeBodyText = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.68);
  line-height: 1.65;
`;

const DangerActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 140px;
  height: 46px;
  padding: 0 20px;
  border: none;
  border-radius: 12px;
  background: #ef4444;
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(239, 68, 68, 0.25);
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(239, 68, 68, 0.32);
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }
`;

const CancelActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 110px;
  height: 46px;
  padding: 0 20px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fff;
  color: rgba(23, 23, 23, 0.72);
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const RevokeSpinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

export const getExpiryInfo = (
  expiresAt?: string | null
): { label: string; isWarning: boolean; isExpired: boolean; hasExpiry: boolean } => {
  if (!expiresAt) {
    return { label: "No expiry", isWarning: false, isExpired: false, hasExpiry: false };
  }
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (isNaN(diff)) {
    return { label: "No expiry", isWarning: false, isExpired: false, hasExpiry: false };
  }
  if (diff <= 0) return { label: "Expired", isWarning: false, isExpired: true, hasExpiry: true };

  const totalMins = Math.floor(diff / 60000);
  const days = Math.floor(totalMins / (24 * 60));
  const hrs = Math.floor((totalMins % (24 * 60)) / 60);
  const mins = totalMins % 60;
  const isWarning = diff <= 24 * 60 * 60 * 1000;

  if (days > 0) {
    const dayStr = `${days} ${days === 1 ? "day" : "days"}`;
    const hrStr = hrs > 0 ? ` ${hrs} ${hrs === 1 ? "hour" : "hours"}` : "";
    return {
      label: `Expires in ${dayStr}${hrStr}`,
      isWarning,
      isExpired: false,
      hasExpiry: true,
    };
  }

  if (hrs > 0) {
    const hrStr = `${hrs} hr`;
    const minStr = mins > 0 ? ` ${mins} min` : "";
    return {
      label: `Expires in ${hrStr}${minStr}`,
      isWarning,
      isExpired: false,
      hasExpiry: true,
    };
  }

  if (mins > 0) {
    return {
      label: `Expires in ${mins} min`,
      isWarning,
      isExpired: false,
      hasExpiry: true,
    };
  }

  return {
    label: "Expires in < 1 min",
    isWarning,
    isExpired: false,
    hasExpiry: true,
  };
};


const CameraToCloudPage: React.FC = () => {
  const [sessions, setSessions] = useState<C2CSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [c2cDisabled, setC2cDisabled] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pendingCreds, setPendingCreds] = useState<{
    session: C2CSession;
    ftps: C2CFtps;
    isNew: boolean;
  } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [activeSession, setActiveSession] = useState<C2CSession | null>(null);
  const [uploadLogs, setUploadLogs] = useState<C2CUploadLog[]>([]);
  const [uploadCount, setUploadCount] = useState(0);
  const [uploadPage, setUploadPage] = useState(1);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [pollError, setPollError] = useState<string | null>(null);
  const [revokeConfirmId, setRevokeConfirmId] = useState<number | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);
  const [editingSession, setEditingSession] = useState<C2CSession | null>(null);
  const [expiringAlerts, setExpiringAlerts] = useState<ExpiringSessionAlertItem[] | null>(null);
  const checkedExpiryRef = useRef(false);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const PAGE_SIZE = 20;

  const handleCloseExpiryAlert = useCallback(() => {
    if (expiringAlerts) {
      for (const item of expiringAlerts) {
        localStorage.setItem(`c2c_expiry_alert_${item.threshold}_${item.session.session_id}`, "true");
      }
      setExpiringAlerts(null);
    }
  }, [expiringAlerts]);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await listC2CSessions();
      const raw: C2CSession[] = res?.data ?? [];
      const sorted = [...raw].sort(
        (a, b) => (b.upload_stats.failed > 0 ? 1 : 0) - (a.upload_stats.failed > 0 ? 1 : 0)
      );
      setSessions(sorted);

      if (!checkedExpiryRef.current) {
        checkedExpiryRef.current = true;
        const alerts: ExpiringSessionAlertItem[] = [];
        const THREE_HOURS_MS = 3 * 60 * 60 * 1000;
        const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

        for (const s of sorted) {
          if (s.status !== "active" || !s.expires_at) continue;
          const diff = new Date(s.expires_at).getTime() - Date.now();
          if (diff <= 0) continue;

          if (diff <= THREE_HOURS_MS) {
            const key = `c2c_expiry_alert_3h_${s.session_id}`;
            if (!localStorage.getItem(key)) {
              alerts.push({
                session: s,
                threshold: "3h",
                expiryLabel: getExpiryInfo(s.expires_at).label,
              });
            }
          } else if (diff <= TWENTY_FOUR_HOURS_MS) {
            const key = `c2c_expiry_alert_24h_${s.session_id}`;
            if (!localStorage.getItem(key)) {
              alerts.push({
                session: s,
                threshold: "24h",
                expiryLabel: getExpiryInfo(s.expires_at).label,
              });
            }
          }
        }

        if (alerts.length > 0) {
          setExpiringAlerts(alerts);
        }
      }
    } catch (err: any) {
      if (err?.response?.status === 503) {
        setC2cDisabled(true);
      }
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const copyToClipboard = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      showErrorToast("Copy failed — please copy manually");
    }
  };

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const fetchSessionAndUploads = useCallback(
    async (sessionId: number, page: number) => {
      try {
        const [sessionRes, uploadsRes] = await Promise.all([
          getC2CSession(sessionId),
          getC2CUploads(sessionId, { page, page_size: PAGE_SIZE }),
        ]);
        const s = sessionRes?.data;
        if (!s || s.status !== "active") {
          stopPolling();
          setPollError("Session is no longer active.");
          if (s) setActiveSession(s);
          return;
        }
        setActiveSession(s);
        setUploadLogs(uploadsRes?.data?.results ?? []);
        setUploadCount(uploadsRes?.data?.count ?? 0);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 404) {
          stopPolling();
          setPollError("Session ended (revoked or expired from another tab).");
        }
      }
    },
    [stopPolling]
  );

  const openUploadView = useCallback(
    (sessionId: number) => {
      stopPolling();
      setPollError(null);
      setUploadPage(1);
      setUploadLogs([]);
      setActiveSessionId(sessionId);
      setUploadLoading(true);
      fetchSessionAndUploads(sessionId, 1).finally(() => setUploadLoading(false));
      pollIntervalRef.current = setInterval(() => {
        fetchSessionAndUploads(sessionId, uploadPage);
      }, 5000);
    },
    [fetchSessionAndUploads, stopPolling, uploadPage]
  );

  const handleViewCredentials = async (sessionId: number) => {
    try {
      const existing = sessions.find((s) => s.session_id === sessionId);
      if (existing?.ftps?.host && existing?.ftps?.username && existing?.ftps?.password) {
        setPendingCreds({ session: existing, ftps: existing.ftps, isNew: false });
        return;
      }
      const res = await getC2CSession(sessionId);
      const session = res.data;
      if (session.ftps) {
        setPendingCreds({ session, ftps: session.ftps, isNew: false });
      } else {
        showErrorToast("Credentials not found for this session.");
      }
    } catch {
      showErrorToast("Could not load credentials.");
    }
  };

  useEffect(() => {
    if (activeSessionId !== null) {
      fetchSessionAndUploads(activeSessionId, uploadPage);
    }
  }, [uploadPage, activeSessionId, fetchSessionAndUploads]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const confirmRevoke = async (sessionId: number) => {
    setRevoking(true);
    try {
      await revokeC2CSession(sessionId);
      showSuccessToast("Session revoked. Camera uploads will be rejected.");
      setRevokeConfirmId(null);
      if (activeSessionId === sessionId) {
        stopPolling();
        setPollError("Session revoked.");
        setActiveSessionId(null);
      }
      loadSessions();
    } catch {
      showErrorToast("Could not revoke session. Please try again.");
    } finally {
      setRevoking(false);
    }
  };

  const totalUploadPages = Math.ceil(uploadCount / PAGE_SIZE);

  const sessionSummary = useMemo(() => {
    const active = sessions.filter(
      (s) => s.status === "active" && !getExpiryInfo(s.expires_at).isExpired
    ).length;
    const uploads = sessions.reduce((sum, s) => sum + s.upload_stats.success, 0);
    const failed = sessions.reduce((sum, s) => sum + s.upload_stats.failed, 0);
    return { active, uploads, failed };
  }, [sessions]);


  if (c2cDisabled) {
    return (
      <C2CPageContainer>
        <DisabledHero>
          <DisabledIconWrap>
            <WifiOff size={34} />
          </DisabledIconWrap>
          <DisabledTitle>Camera-to-Cloud is not enabled</DisabledTitle>
          <DisabledDesc>
            This feature is not enabled on your current plan or server. Please contact support or
            upgrade your plan to access Camera-to-Cloud.
          </DisabledDesc>
        </DisabledHero>
      </C2CPageContainer>
    );
  }

  return (
    <C2CPageContainer>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroText>
            <div className="flex flex-col">
              <HeroBadge>
                <Sparkles size={13} />
                Live uploads
              </HeroBadge>
              <PageTitle className="linear-text">Camera to Cloud</PageTitle>
              <PageSubtitle>
                Connect cameras directly to your albums via FTPS. Photos upload live as you shoot.
              </PageSubtitle>
            </div>
          </HeroText>
          <SubmitButton
            id="c2c-new-session-btn"
            type="button"
            $ready
            onClick={() => setShowCreateModal(true)}
            style={{ minWidth: 160, height: 46 }}
          >
            <Plus size={16} /> New session
          </SubmitButton>
        </HeroContent>
      </Hero>

      {!sessionsLoading && sessions.length > 0 && (
        <SummaryStatsRow>
          <WalletHero>
            <WalletIconWrap>
              <Camera size={16} />
            </WalletIconWrap>
            <WalletHeroBody>
              <WalletHeroValue>{sessionSummary.active}</WalletHeroValue>
              <WalletHeroLabel>Active sessions</WalletHeroLabel>
            </WalletHeroBody>
          </WalletHero>

          <StatCard $tone="#16a34a">
            <StatIcon $tone="#16a34a">
              <CloudUpload size={16} />
            </StatIcon>
            <StatBody>
              <StatValue>{sessionSummary.uploads}</StatValue>
              <StatLabel>Photos uploaded</StatLabel>
            </StatBody>
          </StatCard>

          <StatCard $tone="#ef4444">
            <StatIcon $tone="#ef4444">
              <AlertCircle size={16} />
            </StatIcon>
            <StatBody>
              <StatValue>{sessionSummary.failed}</StatValue>
              <StatLabel>Failed uploads</StatLabel>
            </StatBody>
          </StatCard>
        </SummaryStatsRow>
      )}

      <ListPanel>
        <ListHeader>
          <ListTitle>Active Sessions</ListTitle>
          {!sessionsLoading && sessions.length > 0 && (
            <CountBadge>{sessions.length} active</CountBadge>
          )}
        </ListHeader>

        {sessionsLoading ? (
          <MarketplaceLoadingSpinner padding="48px 0" />
        ) : sessions.length === 0 ? (
          <MarketplaceEmptyState
            icon={<Camera size={36} strokeWidth={1.5} />}
            title="No active sessions"
            description="Create a session to get FTPS credentials for your camera. Photos will appear here live as you shoot and upload."
            actions={[
              {
                label: "Create Your First Session",
                onClick: () => setShowCreateModal(true),
                icon: <Plus size={15} />,
              },
            ]}
            hint={{
              icon: <Sparkles size={13} />,
              text: "Connect cameras directly to albums via FTPS",
            }}
          />
        ) : (
          <SessionsGrid style={{ marginBottom: 0 }}>
            {sessions.map((s) => {
              const expiry = getExpiryInfo(s.expires_at);
              const isExpired = s.status === "expired" || (expiry.hasExpiry && expiry.isExpired);
              return (
                <SessionCard key={s.session_id} $expired={isExpired}>
                  {isExpired && (
                    <ExpiredOverlay>
                      <WifiOff size={14} strokeWidth={2.2} /> Session Expired
                    </ExpiredOverlay>
                  )}
                  {expiry.hasExpiry && expiry.isWarning && !isExpired && (
                    <ExpiryWarning>
                      <AlertTriangle size={14} /> {expiry.label}
                    </ExpiryWarning>
                  )}

                  <CardTopRow>
                    <DeviceIconWrap>
                      <Camera size={22} color="#c94daa" />
                    </DeviceIconWrap>
                    <CardMeta>
                      <DeviceName>{s.device.name}</DeviceName>
                      <DeviceBrand>{s.device.brand}</DeviceBrand>
                    </CardMeta>
                    <CardTools>
                      <ConnTypeBadge>{s.connection_type.toUpperCase()}</ConnTypeBadge>
                      <MenuContainer>
                        <ThreeDotsButton
                          id={`c2c-session-menu-${s.session_id}`}
                          type="button"
                          aria-label="Session actions"
                          aria-expanded={openActionMenuId === s.session_id}
                          onClick={(event) => {
                            event.stopPropagation();
                            setOpenActionMenuId((current) =>
                              current === s.session_id ? null : s.session_id
                            );
                          }}
                        >
                          <Dot />
                          <Dot />
                          <Dot />
                        </ThreeDotsButton>
                        {openActionMenuId === s.session_id && (
                          <>
                            <Backdrop onClick={() => setOpenActionMenuId(null)} />
                            <DropdownMenu onClick={(event) => event.stopPropagation()}>
                              {s.status === "active" && (
                                <>
                                  <MenuItem
                                    id={`c2c-view-creds-${s.session_id}`}
                                    type="button"
                                    onClick={() => {
                                      setOpenActionMenuId(null);
                                      handleViewCredentials(s.session_id);
                                    }}
                                  >
                                    <ItemIconContainer>
                                      <KeyRound size={14} />
                                    </ItemIconContainer>
                                    <ItemLabel>View Credentials</ItemLabel>
                                  </MenuItem>
                                  <MenuItem
                                    id={`c2c-edit-session-${s.session_id}`}
                                    type="button"
                                    onClick={() => {
                                      setOpenActionMenuId(null);
                                      setEditingSession(s);
                                    }}
                                  >
                                    <ItemIconContainer>
                                      <Pencil size={14} />
                                    </ItemIconContainer>
                                    <ItemLabel>Edit Session</ItemLabel>
                                  </MenuItem>
                                </>
                              )}
                              <MenuItem
                                id={`c2c-view-uploads-${s.session_id}`}
                                type="button"
                                onClick={() => {
                                  setOpenActionMenuId(null);
                                  openUploadView(s.session_id);
                                }}
                              >
                                <ItemIconContainer>
                                  <Upload size={14} />
                                </ItemIconContainer>
                                <ItemLabel>View Uploads</ItemLabel>
                              </MenuItem>
                              <MenuItem
                                id={`c2c-revoke-${s.session_id}`}
                                type="button"
                                onClick={() => {
                                  setOpenActionMenuId(null);
                                  setRevokeConfirmId(s.session_id);
                                }}
                              >
                                <ItemIconContainer>
                                  <Trash2 size={14} />
                                </ItemIconContainer>
                                <ItemLabel>Revoke</ItemLabel>
                              </MenuItem>
                            </DropdownMenu>
                          </>
                        )}
                      </MenuContainer>
                    </CardTools>
                  </CardTopRow>
                  <AlbumRow>
                    <AlbumLabel>
                      <FolderOpen size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                      Album:
                    </AlbumLabel>
                    <AlbumValue>{s.album?.name || "—"}</AlbumValue>

                    <AlbumLabel>
                      <FolderOpen size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                      Folder:
                    </AlbumLabel>
                    <AlbumValue>{s.folder?.name || "—"}</AlbumValue>
                  </AlbumRow>


                  <StatsRow>
                    <StatChip $color="#16a34a">
                      <CheckCircle2 size={13} /> {s.upload_stats.success} uploaded
                    </StatChip>
                    {s.upload_stats.failed > 0 && (
                      <StatChip $color="#ef4444">
                        <AlertCircle size={13} /> {s.upload_stats.failed} failed
                      </StatChip>
                    )}
                    {expiry.hasExpiry && !expiry.isWarning && !isExpired && (
                      <StatChip $color="#6b7280">
                        <Clock size={13} /> {expiry.label}
                      </StatChip>
                    )}
                  </StatsRow>


                </SessionCard>
              );
            })}
          </SessionsGrid>
        )}
      </ListPanel>

      {activeSessionId !== null && (
        <UploadProgressPanel
          activeSessionId={activeSessionId}
          activeSession={activeSession}
          pollError={pollError}
          uploadLoading={uploadLoading}
          uploadLogs={uploadLogs}
          uploadPage={uploadPage}
          setUploadPage={setUploadPage}
          totalUploadPages={totalUploadPages}
          onClose={() => {
            stopPolling();
            setActiveSessionId(null);
            setPollError(null);
          }}
          onRevoke={(sessionId) => setRevokeConfirmId(sessionId)}
          onEditDestination={(session) => setEditingSession(session)}
          getExpiryInfo={getExpiryInfo}
        />
      )}

      {showCreateModal && (
        <CreateSessionModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={(session: C2CSessionWithFtps) => {
            setShowCreateModal(false);
            setPendingCreds({ session, ftps: session.ftps, isNew: true });
            loadSessions();
          }}
          setC2cDisabled={setC2cDisabled}
        />
      )}

      {editingSession && (
        <EditSessionModal
          session={editingSession}
          onClose={() => setEditingSession(null)}
          onSuccess={(updatedSession) => {
            setSessions((prev) =>
              prev.map((item) =>
                item.session_id === updatedSession.session_id ? updatedSession : item
              )
            );
            if (activeSessionId === updatedSession.session_id) {
              setActiveSession(updatedSession);
            }
            loadSessions();
          }}
        />
      )}


      {pendingCreds && (
        <CredentialsModal
          pendingCreds={pendingCreds}
          onClose={() => setPendingCreds(null)}
          copyToClipboard={copyToClipboard}
          copied={copied}
        />
      )}

      {revokeConfirmId !== null &&
        createPortal(
          <ModalOverlay
            id="c2c-revoke-confirm-overlay"
            onClick={() => !revoking && setRevokeConfirmId(null)}
          >
            <ModalShell
              style={{ maxWidth: 440 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHero
                style={{
                  background:
                    "linear-gradient(135deg, rgba(239,68,68,0.1) 0%, rgba(245,158,11,0.06) 100%)",
                  borderBottom: "1px solid rgba(239,68,68,0.12)",
                }}
              >
                <HeroRow>
                  <ModalHeroText>
                    <ModalHeroBadge style={{ color: "#ef4444", borderColor: "rgba(239,68,68,0.2)" }}>
                      <AlertTriangle size={11} />
                      Destructive action
                    </ModalHeroBadge>
                    <ModalTitle style={{ color: "#ef4444" }}>Revoke Session?</ModalTitle>
                    <ModalSubtitle>
                      This will immediately disconnect the camera from this session.
                    </ModalSubtitle>
                  </ModalHeroText>
                  <CloseBtn
                    id="c2c-revoke-close"
                    type="button"
                    onClick={() => !revoking && setRevokeConfirmId(null)}
                    aria-label="Close"
                  >
                    <X size={18} />
                  </CloseBtn>
                </HeroRow>
              </ModalHero>
              <ModalBody>
                <RevokeBodyText>
                  The camera will be disconnected immediately. Any in-progress upload will fail, and
                  new uploads from this camera will be rejected.
                </RevokeBodyText>
              </ModalBody>
              <RevokeModalFooter>
                <CancelActionBtn
                  id="c2c-revoke-cancel"
                  type="button"
                  onClick={() => setRevokeConfirmId(null)}
                  disabled={revoking}
                >
                  Cancel
                </CancelActionBtn>
                <DangerActionBtn
                  id="c2c-revoke-confirm"
                  type="button"
                  onClick={() => confirmRevoke(revokeConfirmId)}
                  disabled={revoking}
                >
                  {revoking ? <RevokeSpinner size={16} /> : <Trash2 size={16} />}
                  {revoking ? "Revoking…" : "Yes, Revoke"}
                </DangerActionBtn>
              </RevokeModalFooter>
            </ModalShell>
          </ModalOverlay>,
          document.body
        )}

      {expiringAlerts && expiringAlerts.length > 0 && (
        <ExpiryAlertModal
          alerts={expiringAlerts}
          onClose={handleCloseExpiryAlert}
        />
      )}
    </C2CPageContainer>
  );
};

export default CameraToCloudPage;
